import { useFeatureIsOn } from '@growthbook/growthbook-react';
import cn from 'classnames';
import { useTranslation } from 'next-i18next';

import { FF_FRAMING_PREVIEWER_PRODUCT_PAGE } from '../../../constants/featureFlags';
import { ProductGateway } from '../../../types/AvantArteProduct';
import {
  ProductStoryblok,
  ProductV2Storyblok,
} from '../../../types/generated-storyblok';
import {
  ProductPurchaseDetails,
  ProductPurchasingController,
} from '../../../types/Purchasing';
import { ProductFraming } from './ProductFraming';
import { ProductFramingCustomise } from './ProductFramingCustomise';
import ProductVariants from './ProductVariants';

const ProductVariantOptions = <T extends ProductPurchaseDetails>({
  product,
  productsGateway,
  purchasingController,
}: {
  product: ProductStoryblok | ProductV2Storyblok;
  purchasingController: ProductPurchasingController<T>;
  productsGateway: ProductGateway;
}) => {
  const { t } = useTranslation('product');
  const framingPreviewer = useFeatureIsOn(FF_FRAMING_PREVIEWER_PRODUCT_PAGE);
  // The row is about this work, so it stays put while "Enter draw for set"
  // has the whole collection set selected.
  const productFraming: ProductPurchasingController<T> = {
    ...purchasingController,
    frameOptions:
      purchasingController.productFrameOptions ??
      purchasingController.frameOptions,
    frame:
      purchasingController.productFrame === undefined
        ? purchasingController.frame
        : purchasingController.productFrame,
  };
  const hasFraming = Boolean(productFraming.frameOptions?.length);

  return (
    <div className="flex w-full flex-col gap-3">
      <ProductVariants
        product={product as unknown as ProductType}
        productsGateway={productsGateway}
        purchasing={purchasingController}
      />

      {hasFraming && (
        <div
          className={cn(
            'w-full rounded-sm border border-divider/20 bg-white',
            framingPreviewer ? 'flex flex-col gap-3 p-4' : 'p-3',
          )}
        >
          <div className="flex w-full items-center gap-3">
            <span className="shrink-0">{t('variants.framing')}</span>
            <div className="flex w-full flex-wrap justify-end gap-3">
              <ProductFraming
                product={product as unknown as ProductType}
                productsGateway={productsGateway}
                purchasing={productFraming}
              />
            </div>
          </div>
          {framingPreviewer ? (
            <>
              <hr className="border-divider/20" />
              <ProductFramingCustomise
                product={product}
                purchasingController={purchasingController}
              />
            </>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default ProductVariantOptions;
